import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from 'react-data-table-component';
import { TableColumn } from 'react-data-table-component';
import { compact } from 'lodash';
import { Stack, Button } from 'react-bootstrap';

import { AmountWithDiscount } from '@components/Base';
import { PolicyCommissionInvoiceList } from '@models/PolicyCommissionInvoice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { formatDateTime } from '@utils/datetime';
import { Pagination } from '@models/Pagination';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

type Props = {
  invoices?: PolicyCommissionInvoiceList[] | null;
  pagination?: Pagination;
  isLoading: boolean;
  onShow?: (invoice: PolicyCommissionInvoiceList) => void;
  onChangePage: (page: number) => void;
  onChangePerPage: (page: number) => void;
};

const PolicyCommissionInvoiceListView = ({
  invoices,
  isLoading,
  pagination,
  onShow,
  onChangePage,
  onChangePerPage,
}: Props) => {
  const { t } = useTranslation();
  const canManage = !!onShow;

  const columns = useMemo(() => compact([
    canManage && {
      cell: (invoice) => (
        <Stack direction="horizontal" gap={1}>
          {!!onShow && (
            <Button onClick={() => onShow(invoice)} variant="outline-info" size="sm">
              <FontAwesomeIcon icon={faEye} />
            </Button>
          )}
        </Stack>
      ),
      button: true,
      center: true,
    },
    {
      name: t('common:createdDate'),
      selector: ({ createdAt }) => formatDateTime(createdAt),
      minWidth: '180px',
    },
    {
      name: "Cash flow",
      selector: ({ cashFlow }) => cashFlow,
      minWidth: '120px',
    },
    {
      name: "Payment status",
      selector: ({ paymentStatus }) => paymentStatus,
      minWidth: '140px',
    },
    {
      name: "Total premium",
      selector: ({ totalPremium }) => <AmountWithDiscount finalAmount={totalPremium} skipIncluded />,
      width: '140px',
    },
    {
      name: "Brokerage amount",
      selector: ({ brokerageAmount }) => <AmountWithDiscount finalAmount={brokerageAmount} skipIncluded />,
      width: '140px',
    },
    {
      name: "Total frontline brokerage amount",
      selector: ({ totalFrontlineBrokerageAmount }) => <AmountWithDiscount finalAmount={totalFrontlineBrokerageAmount} skipIncluded />,
      width: '280px',
    },
  ]) as TableColumn<PolicyCommissionInvoiceList>[], [canManage, onShow]);



  return (
    <DataTable
      columns={columns}
      data={invoices ?? []}
      customStyles={customStyles}
      fixedHeader
      pagination
      paginationServer
      progressPending={isLoading}
      paginationPerPage={pagination?.perPage || 10}
      paginationTotalRows={pagination?.total ?? 0}
      onChangePage={onChangePage}
      onChangeRowsPerPage={onChangePerPage}
    />
  );
};

export { PolicyCommissionInvoiceListView };
